/**
 * Dictation anywhere settings — whether the text is pasted, and the prompts a
 * stop can rewrite it with: Default (the start/stop shortcut) and up to nine
 * numbered prompts (Command+1 to Command+9 while listening).
 *
 * Its own store: these settings belong to dictation anywhere only, so voice to
 * Denker and the input-bar mics can never pick them up. Whether the feature is
 * on at all is the disabled state of `shortcut.dictation.anywhere` in the
 * shortcut store, not a flag here.
 *
 * Persisted to localStorage so the choices survive reloads, and mirrored across
 * windows so a change in Settings applies to the next dictation whichever
 * window runs it. Settings saved by an older version are migrated once
 * (`dictation-anywhere-settings-migration.ts`).
 */
import { create } from "zustand";

import {
  MAX_PROMPTS,
  PROMPT_NAME_MAX,
  PROMPT_TEXT_MAX,
} from "@/desktop/dictation-anywhere/polish-prompts";
import { CLEAN_UP_SENTENCE } from "@/desktop/dictation-anywhere/prompt-chips";
import { mirroredStore } from "@/lib/mirrored-store";
import {
  isLegacyDictationAnywhereSettings,
  migrateLegacyDictationAnywhereSettings,
} from "./dictation-anywhere-settings-migration";

const STORAGE_KEY = "denker.dictation-anywhere-settings";
const ID_MAX = 200;

/** A numbered prompt; its number is its place in the list. */
export type DictationAnywhereSavedPrompt = {
  id: string;
  /** Empty shows as "Prompt N". */
  name: string;
  /** Blank stops as spoken. */
  instruction: string;
};

type DictationAnywhereDefaultPrompt = {
  /** Off: the start/stop shortcut delivers the text as spoken. */
  enabled: boolean;
  instruction: string;
};

export type DictationAnywhereSettings = {
  /** Paste into the focused field; off means the text is only copied. */
  autoPaste: boolean;
  defaultPrompt: DictationAnywhereDefaultPrompt;
  prompts: DictationAnywhereSavedPrompt[];
};

export const DICTATION_ANYWHERE_SETTINGS_DEFAULTS: DictationAnywhereSettings = {
  autoPaste: true,
  defaultPrompt: { enabled: false, instruction: CLEAN_UP_SENTENCE },
  prompts: [],
};

function isText(value: unknown, max: number): value is string {
  return typeof value === "string" && value.length <= max;
}

function parseDefaultPrompt(value: unknown): DictationAnywhereDefaultPrompt {
  const defaults = DICTATION_ANYWHERE_SETTINGS_DEFAULTS.defaultPrompt;
  if (!value || typeof value !== "object") return defaults;
  const { enabled, instruction } = value as Record<string, unknown>;
  return {
    enabled: typeof enabled === "boolean" ? enabled : defaults.enabled,
    instruction: isText(instruction, PROMPT_TEXT_MAX)
      ? instruction
      : defaults.instruction,
  };
}

function parsePrompts(value: unknown): DictationAnywhereSavedPrompt[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value
    .flatMap((entry) => {
      if (!entry || typeof entry !== "object") return [];
      const { id, name, instruction } = entry as Record<string, unknown>;
      if (!isText(id, ID_MAX) || !id || seen.has(id)) return [];
      if (!isText(name, PROMPT_NAME_MAX)) return [];
      if (!isText(instruction, PROMPT_TEXT_MAX)) return [];
      seen.add(id);
      return [{ id, name, instruction }];
    })
    .slice(0, MAX_PROMPTS);
}

export function parseDictationAnywhereSettings(
  value: unknown,
): DictationAnywhereSettings {
  if (!value || typeof value !== "object") {
    return DICTATION_ANYWHERE_SETTINGS_DEFAULTS;
  }
  const raw = value as Record<string, unknown>;
  const saved = isLegacyDictationAnywhereSettings(raw)
    ? migrateLegacyDictationAnywhereSettings(raw)
    : raw;
  return {
    autoPaste:
      typeof saved.autoPaste === "boolean"
        ? saved.autoPaste
        : DICTATION_ANYWHERE_SETTINGS_DEFAULTS.autoPaste,
    defaultPrompt: parseDefaultPrompt(saved.defaultPrompt),
    prompts: parsePrompts(saved.prompts),
  };
}

/** Only the settings fields: never a function, whatever the store holds. */
export function pickDictationAnywhereSettings(
  state: DictationAnywhereSettings,
): DictationAnywhereSettings {
  return {
    autoPaste: state.autoPaste,
    defaultPrompt: state.defaultPrompt,
    prompts: state.prompts,
  };
}

function persist(state: DictationAnywhereSettings) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(pickDictationAnywhereSettings(state)),
    );
  } catch {
    // Quota or private-mode failures are non-fatal — ignore.
  }
}

function loadInitial(): DictationAnywhereSettings {
  if (typeof window === "undefined") {
    return DICTATION_ANYWHERE_SETTINGS_DEFAULTS;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DICTATION_ANYWHERE_SETTINGS_DEFAULTS;
    const saved: unknown = JSON.parse(raw);
    const settings = parseDictationAnywhereSettings(saved);
    // Migrated once: storage then holds the current shape only.
    if (
      saved &&
      typeof saved === "object" &&
      isLegacyDictationAnywhereSettings(saved as Record<string, unknown>)
    ) {
      persist(settings);
    }
    return settings;
  } catch {
    return DICTATION_ANYWHERE_SETTINGS_DEFAULTS;
  }
}

/** The editable fields of a numbered prompt. */
type DictationAnywherePromptPatch = Partial<
  Pick<DictationAnywhereSavedPrompt, "name" | "instruction">
>;

type Actions = {
  setAutoPaste: (on: boolean) => void;
  setDefaultEnabled: (on: boolean) => void;
  setDefaultInstruction: (instruction: string) => void;
  /** Appends an empty prompt and returns its id; null once there are nine. */
  addPrompt: () => string | null;
  updatePrompt: (id: string, patch: DictationAnywherePromptPatch) => void;
  /** The prompts after it move up one number. */
  deletePrompt: (id: string) => void;
};

export const useDictationAnywhereSettingsStore = create<
  DictationAnywhereSettings & Actions
>()(
  mirroredStore("dictationAnywhereSettings", (set, get) => ({
    ...loadInitial(),
    setAutoPaste: (autoPaste) => set({ autoPaste }),
    setDefaultEnabled: (enabled) =>
      set({ defaultPrompt: { ...get().defaultPrompt, enabled } }),
    setDefaultInstruction: (instruction) =>
      set({
        defaultPrompt: {
          ...get().defaultPrompt,
          instruction: instruction.slice(0, PROMPT_TEXT_MAX),
        },
      }),
    addPrompt: () => {
      const prompts = get().prompts;
      if (prompts.length >= MAX_PROMPTS) return null;
      const id = `prompt.${crypto.randomUUID()}`;
      set({ prompts: [...prompts, { id, name: "", instruction: "" }] });
      return id;
    },
    updatePrompt: (id, patch) =>
      set({
        prompts: get().prompts.map((prompt) =>
          prompt.id === id
            ? {
                ...prompt,
                ...(patch.name !== undefined && {
                  name: patch.name.slice(0, PROMPT_NAME_MAX),
                }),
                ...(patch.instruction !== undefined && {
                  instruction: patch.instruction.slice(0, PROMPT_TEXT_MAX),
                }),
              }
            : prompt,
        ),
      }),
    deletePrompt: (id) =>
      set({ prompts: get().prompts.filter((prompt) => prompt.id !== id) }),
  })),
);

// Every change is saved, whichever window made it, so a setting changed in
// Settings survives a reload of any window.
useDictationAnywhereSettingsStore.subscribe(persist);

/**
 * Forget the dictation anywhere settings — used by account deletion. The live
 * state goes too: left in place, the next change would write the deleted
 * account's prompts straight back.
 */
export function clearPersistedDictationAnywhereSettings(): void {
  useDictationAnywhereSettingsStore.setState(
    DICTATION_ANYWHERE_SETTINGS_DEFAULTS,
  );
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}
